// eslint-disable-next-line import/enforce-node-protocol-usage
import { Buffer } from 'buffer';

import type OpenAI from 'openai';

import type { OpenAIRealtimeModels } from '@openai/agents-realtime';

import type { SpeakerRoleT, TranscriptItemT, TranscriptT } from 'server/utils';

import type { QuestionWithAnswersT, QuestionnaireT } from './questionnaire';
import type { XmlNodeT } from './xml';

// This is the voice recommended for the best results with Realtime API,
// as of 2025.10.07.
export const DEFAULT_VOICE: VoiceT = 'marin';

export type {
  SpeakerRoleT,
  TranscriptItemT,
  TranscriptT,
};

export const validInsightCategories = [
  'Context',
  'Emotional',
  'Intention',
  'Mental',
  'Physical',
] as const;

/** Insight categories. */
export type InsightCategoryT = typeof validInsightCategories[number];

export type InsightT = {
  category: InsightCategoryT;
  insight: string;
};

/**
 * The known category a model-written category name stands for: the same name
 * in any case, or one a prefix of the other ("emotion", "Emotions"); Context
 * when none matches.
 */
export function normalizeInsightCategory(value: string): InsightCategoryT {
  const needle = value.trim().toLowerCase();
  const exact = validInsightCategories.find(
    (category) => category.toLowerCase() === needle,
  );
  if (exact) return exact;
  const partial = needle.length >= 3
    ? validInsightCategories.find(
      (category) => category.toLowerCase().startsWith(needle)
        || needle.startsWith(category.toLowerCase()),
    )
    : undefined;
  return partial ?? 'Context';
}

export const OPENAI_API_BASE = 'https://api.openai.com/v1';

// NOTE: For the full list of available OpenAI models see:
// https://developers.openai.com/api/docs/models

export const DEFAULT_EMBEDDINGS_MODEL: OpenAI.EmbeddingModel = 'text-embedding-3-small';

// The cheapest model.
export const DEFAULT_GPT_MODEL: OpenAI.ResponsesModel = 'gpt-5-nano';

export const DEFAULT_REALTIME_MODEL: OpenAIRealtimeModels
  = 'gpt-realtime-2.1-mini';

/**
 * audio.input of realtime sessions in wire form, shared by the server-side
 * token mint and the client session config (the SDK re-sends the client config
 * on every agent update, so both must agree).
 */
export const REALTIME_AUDIO_INPUT = {
  // Room / phone-on-table microphones; filtered before the VAD and the model.
  noise_reduction: { type: 'far_field' },

  transcription: { language: 'en', model: 'gpt-realtime-whisper' },

  turn_detection: {
    // A VAD turn creates no reply by itself: the client requests one once
    // the transcription proves speech, so noise and echo never get a reply.
    create_response: false,

    // A speech onset cancels the ongoing response and clears its audio on the
    // server (over WebRTC the output buffer is cleared at onset in any case).
    interrupt_response: true,

    prefix_padding_ms: 300,

    // Quiet that ends a turn; the prompt asks the model to wait for the user
    // through unfinished thoughts.
    silence_duration_ms: 700,

    // Activation level: low-level noise and residual echo stay below it.
    threshold: 0.9,

    type: 'server_vad',
  },
} as const;

/**
 * The tool ending a turn without a spoken reply; its background result makes
 * the SDK request no follow-up response.
 */
export const WAIT_FOR_USER_TOOL_NAME = 'wait_for_user';

/** A question mark, in the scripts the assistant may speak. */
export const QUESTION_MARK = /[?？؟]/;

/**
 * True when a transcription fragment contains letters or digits outside
 * bracketed annotations, as opposed to the artifacts of non-speech audio.
 */
/**
 * A transcript without its annotations of non-speech audio, "[BLANK_AUDIO]",
 * "(silence)" or "*coughs*", their streaming prefixes included.
 */
function withoutAnnotations(text: string): string {
  return text.replace(/\[[^\]]*\]?|\([^)]*\)?|\*[^*]*\*?/g, '');
}

export function isGenuineSpeech(text: string): boolean {
  return (/[\p{L}\p{N}]/u).test(withoutAnnotations(text));
}

function tokenize(text: string): string[] {
  return text
    .toLowerCase()
    .replace(/[\u2018\u2019]/g, "'")
    .match(/[\p{L}\p{N}']+/gu) ?? [];
}

/** Words (letter, digit and apostrophe runs) in a text. */
export function wordCount(text: string): number {
  return tokenize(text).length;
}

/** True when two texts are the same words in the same order. */
export function sameWords(a: string, b: string): boolean {
  const x = tokenize(a);
  const y = tokenize(b);
  return x.length === y.length && x.every((token, i) => token === y[i]);
}

/** Vocal sounds (hums, fillers) the transcriber renders as words. */
const INTERJECTIONS = new Set(
  'ah ahem eh er erm hm hmm hmmm huh mhm mm mmm oh ugh uh uhm um umm'.split(' '),
);

/** True when a transcript is interjections alone ("Hmm.", "Uh, um"). */
export function isInterjectionOnly(text: string): boolean {
  const tokens = tokenize(withoutAnnotations(text));
  return tokens.length > 0 && tokens.every((token) => INTERJECTIONS.has(token));
}

/** Words a text needs before isEchoOfAssistant() can find it an echo. */
export const ECHO_MIN_WORDS = 3;

/**
 * True when a user transcript is, from its first word and in order, mostly the
 * words of the assistant text: the assistant's own audio picked up by the mic.
 * Short answers ("yes") and answers that repeat the assistant's words after
 * their own ("I'm in the bedroom" to "are you in the bedroom?") do not qualify.
 */
export function isEchoOfAssistant(
  userText: string,
  assistantText: string,
): boolean {
  // A leading hum or filler ("Hmm, ...") is not a word of the user's own.
  const user = tokenize(withoutAnnotations(userText));
  while (user.length && INTERJECTIONS.has(user[0] ?? '')) user.shift();
  const reference = tokenize(assistantText);
  if (user.length < ECHO_MIN_WORDS || !reference.length) return false;
  if (!reference.includes(user[0] ?? '')) return false;

  let matches = 0;
  let from = 0;
  for (const token of user) {
    const idx = reference.indexOf(token, from);
    if (idx >= 0) {
      matches += 1;
      from = idx + 1;
    }
  }
  return matches >= 3 && matches / user.length >= 0.6;
}

/** Insights keyed by the id of the transcript item they follow. */
export type InsightsMapT = Record<string, InsightT[]>;

/**
 * Adds the insights not yet in the map under the latest transcript item: the
 * assistant message under construction, or else the user message being
 * answered (a function call can precede the message item of its response).
 * Returns `insights` itself when nothing is new.
 */
export function addInsights(
  insights: InsightsMapT,
  transcript: TranscriptT,
  added: InsightT[],
): InsightsMapT {
  // A reply regenerated for a continued turn repeats its tool calls.
  const known = new Set<string>();
  for (const list of Object.values(insights)) {
    for (const item of list) known.add(`${item.category}/${item.insight}`);
  }
  const fresh = added.filter((item) => {
    const key = `${item.category}/${item.insight}`;
    if (known.has(key)) return false;
    known.add(key);
    return true;
  });
  if (!fresh.length) return insights;

  const anchorId = transcript[transcript.length - 1]?.id ?? 'unanchored';
  return {
    ...insights,
    [anchorId]: [...insights[anchorId] ?? [], ...fresh],
  };
}

/**
 * Voices supported by realtime sessions, see:
 * https://platform.openai.com/docs/api-reference/realtime-sessions/create-realtime-client-secret
 *
 * NOTE: As of 2025.10.07, "martin" and "cedar" voices are recommended for
 * the best quality!
 */
export type RealtimeVoiceT = 'alloy' | 'ash' | 'ballad' | 'cedar' | 'coral'
  | 'echo' | 'marin' | 'sage' | 'shimmer' | 'verse';

/**
 * OpenAI voices. See documentation at:
 * https://platform.openai.com/docs/guides/text-to-speech#voice-options
 * And samples of these voices in the playground: https://www.openai.fm
 *
 * Though, a set of voices supported by OpenAI Realtime API is different,
 * see: https://platform.openai.com/docs/guides/realtime-conversations#voice-options
 * For now, let's just allow here the voices that are supported by both realtime
 * and subscription APIs.
 */
export const VOICES = [
  'alloy',
  'ash',
  'ballad',
  'cedar', // <- Recommended for Realtime sessions.
  'coral',
  'echo',
  // 'fable', -- only in transcription API
  'marin', // <- Recommended for Realtime sessions.
  // 'nova', -- only in transcription API
  // 'onyx', -- only in transcription API
  'sage',
  'shimmer',
  // 'verse', -- only in realtime API.
] as const;

export type VoiceT = typeof VOICES[number];

type CalcCachedSpeechPathOptionsT = {
  voice?: VoiceT;
};

/**
 * Calculates the Firebase.Storage path of the cached speech file for the given
 * text, with the given options.
 */
export async function calcCachedSpeechPath(text: string, {
  voice = DEFAULT_VOICE,
}: CalcCachedSpeechPathOptionsT = {}): Promise<string> {
  const key = `${voice}/${text}`;
  const buffer = await crypto.subtle.digest('SHA-1', Buffer.from(key));
  const hash = Buffer.from(buffer).toString('hex');
  return `speech/${hash}.m4a`;
}

export function findLastMessageFrom(
  transcript: TranscriptT,
  role: SpeakerRoleT,
): TranscriptItemT | null {
  for (let i = transcript.length - 1; i >= 0; --i) {
    const item = transcript[i];
    if (!item) throw Error('Internal error');

    if (item.role === role) return item;
  }

  return null;
}

/**
 * Finds the index of transcript item by ID.
 */
export function findIndexById(
  transcript: TranscriptT,
  id: string,
): number {
  // NOTE: We assume this function is mostly used to find one of the latest
  // transcript items, thus we do the look-up in the reverse direction of
  // the array.
  for (let idx = transcript.length - 1; idx >= 0; --idx) {
    if (transcript[idx]?.id === id) return idx;
  }

  return -1;
}

/**
 * Finds the transcript item by ID.
 */
export function findById(
  transcript: TranscriptT,
  id: string,
): TranscriptItemT | null {
  // NOTE: We assume this function is mostly used to find one of the latest
  // transcript items, thus we do the look-up in the reverse direction of
  // the array.
  for (let idx = transcript.length - 1; idx >= 0; --idx) {
    const item = transcript[idx];
    if (item?.id === id) return item;
  }

  return null;
}

export const XML_TRANSCRIPT_V2 = 'transcript-v2';

export type XmlTranscriptV2 = XmlNodeT<typeof XML_TRANSCRIPT_V2>;

const TRANSCRIPT_DESCRIPTION = 'description="Transcript of a conversation between \'assistant\' (you) and the \'user\'."';

/**
 * Converts the given conversation transcript into an AI-friendly XML string.
 *
 * @param conversation - Conversation transcript.
 * @param timestamp - User's local date & time.
 * @param indent - Optional. The base indent of generated text lines.
 */
export function toXmlTranscript(
  conversation: QuestionWithAnswersT[] | QuestionnaireT | TranscriptT,
  timestamp: string,
  indent = 0,
): XmlTranscriptV2 {
  const ind = ' '.repeat(indent);
  const items = Array.isArray(conversation) ? conversation : conversation.items;

  let xml = `${ind}<transcript ${TRANSCRIPT_DESCRIPTION} date-and-time="${timestamp}">\n`;
  for (const item of items ?? []) {
    if ('question' in item) {
      xml += `${ind}  <assistant>${item.question}</assistant>\n`;
      xml += `${ind}  <user>${item.answers.map((x) => x[1]).join('; ')}</user>\n`;
    } else {
      xml += `${ind}  <${item.role}>${item.message}</${item.role}>\n`;
    }
  }
  xml += `${ind}</transcript>\n\n`;

  return { kind: 'transcript-v2', xml };
}
